import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const fetchUserProfile = createAsyncThunk(
  "profile/fetchUserProfile",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { token, userId } = getState().auth;

      if (!token) return rejectWithValue("No token found. Please login again.");
      if (!userId) return rejectWithValue("No userId found in token.");

      const url =
        "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod/Profile/GetUserProfile" +
        `?userId=${encodeURIComponent(userId)}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: token,
        },
      });

      if (!res.ok) {
        let msg = `Request failed (${res.status})`;
        try {
          const data = await res.json();
          msg = data?.message || data?.error || msg;
        } catch {}
        return rejectWithValue(msg);
      }

      const data = await res.json();
      return data;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to load profile");
    }
  }
);

// profileSlice.js (add new thunk)
export const fetchUserById = createAsyncThunk(
  "profile/fetchUserById",
  async (userId, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().auth;
      if (!token) return rejectWithValue("No token found");

      const url =
        "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod/Profile/GetUserProfile" +
        `?userId=${encodeURIComponent(userId)}`;

      const res = await fetch(url, {
        method: "GET",
        headers: { Authorization: token },
      });

      if (!res.ok) {
        let msg = `Request failed (${res.status})`;
        try {
          const data = await res.json();
          msg = data?.message || data?.error || msg;
        } catch {}
        return rejectWithValue(msg);
      }

      return await res.json();
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to load user");
    }
  }
);

const profileSlice = createSlice({
  name: "profile",
  initialState: {
    data: null,
    status: "idle", // idle | loading | succeeded | failed
    byId: {}, // ✅ cache details { [userId]: userObj }
    byIdStatus: "idle", // loading/succeeded/failed
    error: "",
  },
  reducers: {
    clearProfile: (state) => {
      state.data = null;
      state.status = "idle";
      state.error = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to load profile";
      })
      .addCase(fetchUserById.pending, (state) => {
        state.byIdStatus = "loading";
        state.byIdError = "";
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.byIdStatus = "succeeded";
        const user = action.payload;
        if (user?.userId) state.byId[user.userId] = user; // cache
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.byIdStatus = "failed";
        state.byIdError = action.payload || "Failed to load user";
      });
  },
});

export const { clearProfile } = profileSlice.actions;
export default profileSlice.reducer;

// selectors
export const selectProfile = (s) => s.profile.data;
export const selectProfileStatus = (s) => s.profile.status;
export const selectProfileError = (s) => s.profile.error;
export const selectUserByIdMap = (s) => s.profile.byId;
export const selectUserByIdStatus = (s) => s.profile.byIdStatus;
export const selectUserByIdError = (s) => s.profile.byIdError;
